import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight } from 'lucide-react';

const FORTUNES = [
  { message: 'A powerful Amethyst is seeking you. Your third eye is ready to open.', link: '/healing-prescription', cta: 'Get Your Crystal Rx — $125', color: '#9D4EDD' },
  { message: 'The Root Chakra stirs beneath you. Ground yourself before your next big move.', link: '/root-for-your-team', cta: 'Activate Root Energy', color: '#E74C3C' },
  { message: 'A frequency only you can hear is waiting. 528 Hz — the miracle tone.', link: '/chakras', cta: 'Enter the Frequency Lab — $44.44', color: '#FFB800' },
  { message: 'Your zodiac crystal is calling. The stars have aligned for transformation.', link: '/healing-prescription', cta: 'Discover Your Prescription — $125', color: '#FF6B9D' },
  { message: 'Selenite light surrounds you. A season of clarity and angelic guidance begins.', link: '/collection', cta: 'View the Sacred Six', color: '#00B4D8' },
  { message: 'Rose Quartz whispers: love yourself first. The rest will follow.', link: '/collection', cta: 'Explore Rose Quartz', color: '#FF6B9D' },
  { message: 'A rare Green Aventurine crosses your path. Prosperity is near.', link: '/collection', cta: 'Claim Your Crystal', color: '#00C853' },
  { message: 'The universe says: invest in your energy. It pays dividends forever.', link: '/healing-prescription', cta: 'Start Healing — $125', color: '#c9a96e' },
];

const CrystalBall = () => {
  const [gazing, setGazing] = useState(false);
  const [fortune, setFortune] = useState(null);
  const [glowIntensity, setGlowIntensity] = useState(0);

  const gaze = () => {
    if (gazing) return;
    setGazing(true);
    setFortune(null);

    let intensity = 0;
    const interval = setInterval(() => {
      intensity += 5;
      setGlowIntensity(intensity);
      if (intensity >= 100) {
        clearInterval(interval);
        setFortune(FORTUNES[Math.floor(Math.random() * FORTUNES.length)]);
        setGazing(false);
      }
    }, 50);
  };

  return (
    <section className="py-24 px-6 bg-[#1e1919] relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#9D4EDD]/10 rounded-full blur-[100px]" />
      <div className="absolute top-1/4 right-1/4 w-[300px] h-[300px] bg-[#FF6B9D]/8 rounded-full blur-[80px]" />

      <div className="max-w-3xl mx-auto text-center relative z-10">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
          <Sparkles className="w-3.5 h-3.5 text-[#FFB800]" />
          <span className="text-xs text-[#FFB800] uppercase tracking-[4px] font-bold">Gaze Into Your Future</span>
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl text-white mb-3">The Crystal Ball</h2>
        <p className="text-white/50 text-sm mb-12">Touch the orb and receive a message from the crystal realm.</p>

        {/* Crystal Ball */}
        <div className="flex justify-center mb-10">
          <button
            onClick={gaze}
            disabled={gazing}
            className="relative group cursor-pointer"
            data-testid="crystal-ball-btn"
          >
            {/* Outer glow ring */}
            <div
              className="absolute -inset-6 rounded-full transition-all duration-500"
              style={{
                background: `radial-gradient(circle, rgba(157,78,221,${glowIntensity * 0.004}) 0%, rgba(255,107,157,${glowIntensity * 0.003}) 40%, transparent 70%)`,
                transform: gazing ? 'scale(1.3)' : 'scale(1)',
              }}
            />
            {/* Ball */}
            <div
              className="relative w-48 h-48 sm:w-56 sm:h-56 rounded-full overflow-hidden transition-transform duration-300 group-hover:scale-105"
              style={{
                background: `radial-gradient(circle at 35% 30%, rgba(255,255,255,0.25) 0%, rgba(157,78,221,0.3) 30%, rgba(108,92,231,0.5) 50%, rgba(30,25,25,0.9) 80%)`,
                boxShadow: `0 0 ${20 + glowIntensity * 0.8}px ${glowIntensity * 0.4}px rgba(157,78,221,${0.2 + glowIntensity * 0.005}), inset 0 0 60px rgba(157,78,221,0.2), 0 20px 60px rgba(0,0,0,0.5)`,
              }}
            >
              {/* Glass reflection */}
              <div className="absolute top-4 left-8 w-16 h-8 bg-white/20 rounded-full blur-md rotate-[-30deg]" />
              <div className="absolute top-10 left-12 w-6 h-3 bg-white/30 rounded-full blur-sm rotate-[-30deg]" />

              {/* Inner mist */}
              {gazing && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-32 h-32 rounded-full bg-[#9D4EDD]/30 blur-xl animate-pulse" />
                </div>
              )}

              {/* Center content */}
              <div className="absolute inset-0 flex items-center justify-center">
                {!fortune && !gazing && (
                  <span className="text-white/60 text-xs uppercase tracking-[3px] font-bold">Touch</span>
                )}
                {gazing && (
                  <div className="w-8 h-8 border-2 border-white/40 border-t-[#FFB800] rounded-full animate-spin" />
                )}
              </div>
            </div>
            {/* Base/pedestal */}
            <div className="relative mx-auto -mt-3 w-32 h-6 bg-gradient-to-b from-[#3a3535] to-[#2a2525] rounded-b-xl"
              style={{ clipPath: 'polygon(10% 0%, 90% 0%, 100% 100%, 0% 100%)' }} />
          </button>
        </div>

        {/* Fortune Result */}
        {fortune && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-700" data-testid="crystal-ball-fortune">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 max-w-md mx-auto mb-6">
              <p className="font-serif text-xl text-white italic leading-relaxed mb-6">
                &ldquo;{fortune.message}&rdquo;
              </p>
              <Link
                to={fortune.link}
                className="inline-flex items-center gap-2 px-8 py-3 rounded-xl font-bold text-white text-sm hover:scale-105 transition-all"
                style={{ background: `linear-gradient(135deg, ${fortune.color}, ${fortune.color}cc)`, boxShadow: `0 8px 30px ${fortune.color}40` }}
                data-testid="fortune-cta"
              >
                {fortune.cta} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <button
              onClick={() => { setFortune(null); setGlowIntensity(0); }}
              className="text-sm text-white/40 hover:text-white/70 transition-colors"
            >
              Gaze again
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default CrystalBall;
